import { useState } from "react";
import { Link, Redirect, useLocalSearchParams } from "expo-router";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, FormTitle, InputWithLabel, Layout, Logo } from "@ptsd/uikit";
import { ArrowRight } from "@tamagui/lucide-icons";
import i18next from "i18next";
import type { SubmitHandler } from "react-hook-form";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Form, Image, Text, useMedia, View } from "tamagui";
import z from "zod";

import { api } from "~/utils/api";
import { authClient } from "~/utils/auth";
import { GlobalErrorScreen } from "~/components/blocks";
import { Footer, Header } from "~/components/layout";

export const schema = z.object({
  email: z
    .string()
    .nonempty(i18next.t("authValidationMessages:general.required"))
    .email(i18next.t("authValidationMessages:email.invalid-format")),

  password: z
    .string()
    .nonempty(i18next.t("authValidationMessages:general.required"))
    .min(8, i18next.t("authValidationMessages:password.min-length"))
    .regex(
      /^(?=.*[A-Z])[A-Za-z0-9!@#$%^&*()_+{}[\]:;<>,.?/\\~-]{8,}$/,
      i18next.t("authValidationMessages:password.min-length"),
    ),
});

export type FormData = z.infer<typeof schema>;

//eslint-disable-next-line @typescript-eslint/no-var-requires
const img = require("../../assets/signup/signup-2.png");

//TODO: update i18n

const Signin = () => {
  const { error: globalError } = useLocalSearchParams<{ error: string }>();
  const { t } = useTranslation("signin");
  const { sm } = useMedia();

  if (globalError === "unable_to_create_user") {
    return (
      <GlobalErrorScreen error={t("error-messages.unable-to-create-user")} />
    );
  }

  return (
    <Layout variant="dualbackground">
      {sm && <Header />}
      <View
        $sm={{
          flex: 1,
        }}
        flex={2}
        flexDirection="row"
        width={"100%"}
      >
        <View
          paddingHorizontal={"$2.5"}
          flex={1}
          alignItems="flex-end"
          width={"50%"}
          $gtSm={{
            paddingRight: 110,
          }}
          $sm={{
            alignItems: "center",
          }}
        >
          {!sm && (
            <View width={"100%"} paddingTop={"$5.8"} maxWidth={413}>
              <Logo
                style={{
                  width: 214,
                  height: 36,
                }}
              />
            </View>
          )}

          <SigninForm />
        </View>
        {!sm && (
          <View
            paddingHorizontal={"$2.5"}
            width={"50%"}
            alignItems="flex-start"
            paddingLeft={110}
            paddingTop={200}
            justifyContent="flex-start"
            flex={1}
          >
            <Image
              maxHeight={560}
              maxWidth={600}
              src={img}
              contain="size"
              width="100%"
              resizeMode="contain"
              aspectRatio={1}
            />
          </View>
        )}
      </View>
      {sm && <Footer />}
    </Layout>
  );
};

export default Signin;

const SigninForm = () => {
  const { t } = useTranslation("signin");
  const { sm } = useMedia();
  const [isLoading, setIsLoading] = useState(false);
  const [isAuthorized, { refetch }] =
    api.auth.isAuthorized.useSuspenseQuery(undefined);

  const { setError, control, handleSubmit } = useForm<FormData>({
    defaultValues: {
      email: "",
      password: "",
    },
    resolver: zodResolver(schema),
  });

  const onSubmit: SubmitHandler<FormData> = (data) => {
    if (isLoading) {
      return;
    }
    void (async () => {
      setIsLoading(true);
      const result = await authClient.signIn.email({
        email: data.email,
        password: data.password,
        rememberMe: true,
      });
      if (result.error?.code) {
        setError("password", {
          message: t("error-messages.incorrect-credentials"),
        });
      } else {
        await refetch();
      }
      setIsLoading(false);
    })();
  };

  if (isAuthorized) {
    return <Redirect href={"/"} />;
  }

  return (
    <Form
      width={"100%"}
      maxWidth={413}
      gap={"$4"}
      paddingTop={"$15"}
      paddingBottom={"$3"}
      $sm={{
        paddingVertical: "$4",
      }}
      flex={1}
      justifyContent="flex-start"
      onSubmit={handleSubmit(onSubmit)}
    >
      <View width={"100%"} gap={"$5"}>
        <FormTitle>{t("form.title")}</FormTitle>
        <View width={"100%"} gap={"$2"}>
          <View width={"100%"} gap={"$4"}>
            <Controller
              control={control}
              name={"email"}
              rules={{
                required: true,
              }}
              render={({ field, fieldState }) => {
                return (
                  <InputWithLabel
                    data-id={"signin-email"}
                    variant="inline"
                    size="md"
                    isError={!!fieldState.error?.message}
                    error={fieldState.error?.message}
                    label={t("form.email.label")}
                    placeholder={t("form.email.placeholder")}
                    onChangeText={(text) => field.onChange(text)}
                    {...field}
                  />
                );
              }}
            />

            <Controller
              control={control}
              name={"password"}
              rules={{
                required: true,
              }}
              render={({ field, fieldState }) => (
                <InputWithLabel
                  data-id={"signin-password"}
                  secureTextEntry
                  variant="inline"
                  size="md"
                  isError={!!fieldState.error?.message}
                  error={fieldState.error?.message}
                  label={t("form.password.label")}
                  placeholder={t("form.password.placeholder")}
                  onChangeText={(text) => field.onChange(text)}
                  {...field}
                />
              )}
            />
          </View>

          <Link
            data-id={"signin-forgot-password"}
            href={"/auth/forgot-password"}
            asChild
          >
            <Text
              fontFamily={"$body"}
              fontSize={"$3"}
              color={"$gray-primary-900"}
              hoverStyle={{ color: "$lightblue-800" }}
              textDecorationLine="underline"
            >
              {t("form.forgot-pass-link")}
            </Text>
          </Link>
        </View>
      </View>

      <View
        gap={"$5"}
        $sm={{
          gap: "$4",
        }}
      >
        <Form.Trigger asChild>
          <Button
            data-id={"signin-submit"}
            iconAfter={<ArrowRight color={"white"} size={"$2.5"} />}
            variant="primary"
            size={sm ? "md" : "lg"}
            rounded="lg"
          >
            {isLoading ? t("form.submit.loading") : t("form.submit.label")}
          </Button>
        </Form.Trigger>
      </View>
    </Form>
  );
};
